import { useEffect, useState } from 'react'
import Button from '@mui/material/Button'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import MuiLink from '@mui/material/Link'
import Modal from './Modal'
import { findPlace, placeAt } from '../lib/geocode'
import { placeName, type MapPoint } from '../utilities/setPlaces'

/** A location or the home icon that the reader dropped at a new place (`mapCore.ts` `moveNodes`). */
export interface NodesMove {
  id: string
  nodeIds: string[]
  label: string
  place: MapPoint
}

/** What a right-click asks to put back (`mapCore.ts` `resetNodes`, `resetCloud`). */
export type PlaceReset = { nodeIds: string[]; label: string } | { cloud: CloudPlace; label: string }

const count = (n: number) => (n === 1 ? '1 node' : `${n} nodes`)

/**
 * Where the reader dropped a location (nobgp-router#1976): the name of the place, looked up from the
 * point and changed by the reader if it is wrong, or a place found by its name or an address. Save
 * stores the place for each node of the location.
 *
 * ⚠ The router stores the point rounded to about 1 km, and the city, state and country. The text the
 * reader types to find a place goes to OpenStreetMap only (`lib/geocode.ts`).
 */
export function MoveNodesDialog({
  move,
  onMoveTo,
  onSave,
  onCancel,
}: {
  move: NodesMove
  /** The reader found another point by its name: the map draws the location there. */
  onMoveTo: (point: MapPoint) => void
  onSave: (place: { lat: number; lng: number; city: string; state: string; country: string }) => Promise<string | null>
  onCancel: () => void
}) {
  const [point, setPoint] = useState(move.place)
  const [city, setCity] = useState('')
  const [state, setState] = useState('')
  const [country, setCountry] = useState('')
  const [search, setSearch] = useState('')
  const [busy, setBusy] = useState<'lookup' | 'save' | null>('lookup')
  const [error, setError] = useState<string | null>(null)

  // The name of the place where it was dropped. A lookup that fails leaves the fields empty.
  useEffect(() => {
    let live = true
    placeAt(move.place)
      .then(found => {
        if (!live || !found) return
        setCity(found.city)
        setState(found.state)
        setCountry(found.country)
      })
      .catch(() => live && setError('The name of this place could not be looked up. Type it below.'))
      .finally(() => live && setBusy(null))
    return () => {
      live = false
    }
  }, [move.place])

  const find = async () => {
    // ⚠ Also for Enter, which the disabled Find button does not stop: a search that ends before the
    // lookup of the dropped point gets its names written over by that lookup.
    if (!search.trim() || busy !== null) return
    setBusy('lookup')
    setError(null)
    try {
      const found = await findPlace(search.trim())
      if (!found) {
        setError(`No place was found for "${search.trim()}".`)
        return
      }
      setPoint(found.point)
      setCity(found.city)
      setState(found.state)
      setCountry(found.country)
      onMoveTo(found.point)
    } catch {
      setError('The place lookup did not answer. Try again, or type the name below.')
    } finally {
      setBusy(null)
    }
  }

  const save = async () => {
    setBusy('save')
    setError(null)
    const failed = await onSave({ ...point, city: city.trim(), state: state.trim(), country: country.trim() })
    setBusy(null)
    if (failed) setError(failed)
  }

  const name = placeName({ city, state, country })
  return (
    <Modal open onClose={onCancel} maxWidth="xs">
      <DialogTitle variant="subtitle1">Move {count(move.nodeIds.length)} from {move.label}</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Use this when the place of the address is wrong, for example on Starlink or a VPN. Every member of the org sees
            the nodes here.
          </Typography>
          <Stack direction="row" spacing={1}>
            <TextField
              size="small"
              fullWidth
              label="Find a place or an address"
              value={search}
              onChange={e => setSearch(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') void find()
              }}
            />
            <Button variant="outlined" onClick={() => void find()} disabled={busy !== null || !search.trim()}>
              Find
            </Button>
          </Stack>
          <TextField size="small" label="City" value={city} onChange={e => setCity(e.target.value)} slotProps={{ htmlInput: { maxLength: 100 } }} />
          <Stack direction="row" spacing={1}>
            <TextField size="small" label="State" value={state} onChange={e => setState(e.target.value)} slotProps={{ htmlInput: { maxLength: 100 } }} />
            <TextField size="small" label="Country" value={country} onChange={e => setCountry(e.target.value)} slotProps={{ htmlInput: { maxLength: 100 } }} />
          </Stack>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            noBGP keeps the place to about 1 km, and its name. It never keeps an address. Place names from{' '}
            {/* ⚠ The credit OpenStreetMap's licence (ODbL) asks for where its data is shown. */}
            <MuiLink href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer" color="inherit">
              © OpenStreetMap contributors
            </MuiLink>
            .
          </Typography>
          {error && (
            <Typography variant="body2" sx={{ color: 'error.main' }}>
              {error}
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="text" sx={{ color: 'GrayText' }} onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="contained" onClick={() => void save()} disabled={busy !== null}>
          {name ? `Move to ${name}` : 'Move here'}
        </Button>
      </DialogActions>
    </Modal>
  )
}

/** Put a placed location back at the place of its address, or let the map place a cloud again. */
export function ResetPlaceDialog({
  reset,
  onConfirm,
  onCancel,
}: {
  reset: PlaceReset
  onConfirm: () => Promise<string | null>
  onCancel: () => void
}) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const cloud = 'cloud' in reset
  const confirm = async () => {
    setBusy(true)
    const failed = await onConfirm()
    setBusy(false)
    if (failed) setError(failed)
  }
  return (
    <Modal open onClose={onCancel} maxWidth="xs">
      <DialogTitle variant="subtitle1">
        {cloud ? `Let the map place ${reset.label} again?` : `Put ${count(reset.nodeIds.length)} back at the place of the address?`}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {cloud
            ? 'The map puts it near the locations of its networks, for every member of the org.'
            : `The nodes of ${reset.label} are shown again where their public address is, for every member of the org.`}
        </Typography>
        {error && (
          <Typography variant="body2" sx={{ color: 'error.main', mt: 1 }}>
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="text" sx={{ color: 'GrayText' }} onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="contained" onClick={() => void confirm()} disabled={busy}>
          {cloud ? 'Let the map place it' : 'Put back'}
        </Button>
      </DialogActions>
    </Modal>
  )
}
